import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import QuickViewModal from "./QuickViewModal";

const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL;
const ProductCard = ({ allProducts, productIsLoading, productError }) => {
  const navigate = useNavigate();

  const [selectedVariants, setSelectedVariants] = useState({});
  const [selectedVariantDetails, setSelectedVariantDetails] = useState({});

  const [quickViewProduct, setQuickViewProduct] = useState(null);

  useEffect(() => {
    const variants = {};
    const variantDetails = {};
    allProducts?.forEach((product) => {
      if (product.is_variant === "false") {
        variantDetails[product.id] = {
          price: product.price,
          discount_price: product.discount_price,
        };
      } else {
        const allVariants = product.variant_combinations || [];
        const firstInStockVariant = allVariants.find(
          (combo) => combo.stock_quantity > 0 && combo.stock_status === "in_stock"
        );
        const variantToSelect = firstInStockVariant || allVariants[0];

        if (variantToSelect) {
          variants[product.id] = variantToSelect.id;
          variantDetails[product.id] = {
            price: variantToSelect.price,
            discount_price: variantToSelect.discount_price,
          };
        }
      }
    });
    setSelectedVariants(variants);
    setSelectedVariantDetails(variantDetails);
  }, [allProducts]);

  const openQuickView = (product) => {
    setQuickViewProduct(product);
  };

  const onCloseQuickView = () => {
    setQuickViewProduct(null);
  };

  if (productIsLoading)
    return <div className="text-center text-gray-500 p-4">Loading products...</div>;
  if (productError)
    return (
      <div className="text-center text-red-500 p-4">
        Oops! Something went wrong. Please try again later.
      </div>
    );

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-4">
        {allProducts?.map((product) => {
          const currentPriceInfo = product.is_variant === "true" && selectedVariantDetails[product.id]
            ? selectedVariantDetails[product.id]
            : { price: product.price, discount_price: product.discount_price };

          const displayPrice = parseFloat(currentPriceInfo.price || 0);
          const displayDiscountPrice = parseFloat(currentPriceInfo.discount_price || currentPriceInfo.price || 0);

          return (
            <div
              key={product.id}
              className="flex flex-col justify-between relative h-full rounded-xl border border-gray-200 p-4 shadow-sm bg-white overflow-hidden group"
            >
              {displayDiscountPrice < displayPrice && (
                <div className="absolute top-3 left-3 bg-[#00A762] text-white px-3 py-1 text-xs font-bold rounded-lg z-10 shadow">
                  {Math.round(
                    ((displayPrice - displayDiscountPrice) / displayPrice) * 100
                  )}
                  % OFF
                </div>
              )}

              <div
                className="cursor-pointer flex-shrink-0 mb-4 overflow-hidden rounded-lg"
                onClick={() => navigate(`/shop/${product?.id}`)}
              >
                <img
                  src={
                    product?.additional_images?.[0]
                      ? BASE_IMAGE_URL + product.additional_images[0]
                      : "https://placehold.co/200x200/e5e7eb/555555?text=Product+Image"
                  }
                  alt={product?.name || "Product Image"}
                  className="w-full h-48 object-contain transition-transform duration-300 group-hover:scale-105"
                />
              </div>

              <div className="flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    {product?.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {product?.description}
                  </p>
                </div>

                <div className="flex items-baseline gap-2 mb-4">
                  {displayDiscountPrice < displayPrice ? (
                    <>
                      <span className="text-gray-500 line-through text-sm">
                        ₹{displayPrice.toFixed(2)}
                      </span>
                      <span className="text-xl font-bold text-[#00A762]">
                        ₹{displayDiscountPrice.toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <span className="text-xl font-bold text-gray-900">
                      ₹{displayPrice.toFixed(2)}
                    </span>
                  )}
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openQuickView(product);
                  }}
                  className="flex items-center text-[#00A762] border justify-center w-full rounded-lg shadow-md font-dm px-3 py-2 capitalize cursor-pointer border-[#00A762] hover:bg-[#E6F4EB]"
                >
                  Quick View
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {quickViewProduct && (
        <QuickViewModal product={quickViewProduct} onClose={onCloseQuickView} />
      )}
    </>
  );
};

export default ProductCard;